(() => {
  if (customElements.get('entina-video-features')) return;
  class EntinaVideoFeatures extends HTMLElement {
    connectedCallback() {
      if (this.connected || this.initializing) return;
      this.initializing = true;
      queueMicrotask(() => {
        this.initializing = false;
        if (!this.isConnected) return;
        if (!this.querySelector('[data-control]')) {
          this.contentObserver?.disconnect();
          this.contentObserver = new MutationObserver(() => {
            if (this.querySelector('[data-control]')) {
              this.contentObserver.disconnect();
              this.connectedCallback();
            }
          });
          this.contentObserver.observe(this, {childList:true,subtree:true});
          return;
        }
        this.initialize();
      });
    }
    initialize() {
      if (this.connected) return;
      this.connected = true;
      this.abort = new AbortController();
      this.items = Array.from(this.querySelectorAll('[data-item]')).filter(item => item.closest('entina-video-features') === this);
      this.tabs = this.items.map(item => item.querySelector('[data-tab]'));
      this.panels = this.tabs.map(tab => this.querySelector('#' + CSS.escape(tab.getAttribute('aria-controls'))));
      this.stage = this.querySelector('[data-stage]');
      this.control = this.querySelector('[data-control]');
      this.overlay = this.querySelector('[data-overlay]');
      this.status = this.querySelector('[data-status]');
      this.index = -1;
      this.generation = 0;
      this.playEpoch = 0;
      this.frame = null;
      this.inView = false;
      this.started = false;
      this.userPaused = false;
      this.durationLimit = Number(this.dataset.clipDuration) || 10;
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.requiresGesture = this.dataset.autoplay !== 'true' || this.reduce.matches;
      const signal = this.abort.signal;
      this.panels.forEach(panel => this.stage.insertBefore(panel, this.overlay));
      this.items.forEach((item, index) => {
        this.tabs[index].addEventListener('click', () => this.select(index, true), {signal});
        this.tabs[index].addEventListener('keydown', event => this.keydown(event, index), {signal});
      });
      this.control.addEventListener('click', () => this.toggle(), {signal});
      this.querySelector('[data-start]').addEventListener('click', () => this.resume(), {signal});
      document.addEventListener('visibilitychange', () => this.reconcile(), {signal});
      window.addEventListener('pagehide', () => { this.suspended = true; this.reconcile(); }, {signal});
      window.addEventListener('pageshow', () => { this.suspended = false; this.reconcile(); }, {signal});
      this.mobile.addEventListener('change', () => this.orientation(), {signal});
      this.reduce.addEventListener('change', () => {
        if (this.reduce.matches) this.requiresGesture = true;
        this.reconcile();
      }, {signal});
      document.addEventListener('shopify:block:select', event => {
        const index = this.items.findIndex(item => item.dataset.blockId === String(event.detail?.blockId));
        if (index !== -1) this.select(index, true);
      }, {signal});
      this.orientation();
      this.dataset.readyState = 'ready';
      if (!this.items.length) {
        this.control.disabled = true;
        this.setState('empty');
        return;
      }
      const firstPlayable = this.nextPlayable(-1);
      this.select(firstPlayable === -1 ? 0 : firstPlayable);
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        this.inView = entry.isIntersecting && entry.intersectionRatio >= .15;
        if (this.inView && !this.started) {
          this.started = true;
          this.mountVideo();
        }
        this.reconcile();
      }, {threshold:[0,.15]});
      this.observer.observe(this.stage);
    }
    nextPlayable(after) {
      for (let step = 1; step <= this.panels.length; step++) {
        const index = (after + step) % this.panels.length;
        if (this.panels[index].dataset.videoUrl?.trim()) return index;
      }
      return -1;
    }
    emptyState() {
      return this.nextPlayable(-1) === -1 ? 'missing' : 'empty';
    }
    orientation() {
      this.querySelector('[role="tablist"]').setAttribute('aria-orientation', this.mobile.matches ? 'horizontal' : 'vertical');
    }
    keydown(event, index) {
      const previous = this.mobile.matches ? 'ArrowLeft' : 'ArrowUp';
      const next = this.mobile.matches ? 'ArrowRight' : 'ArrowDown';
      let target;
      if (event.key === previous) target = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === next) target = (index + 1) % this.tabs.length;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = this.tabs.length - 1;
      if (target === undefined) return;
      event.preventDefault();
      this.select(target, true);
      this.tabs[target].focus({preventScroll:true});
    }
    select(index, userAction = false) {
      this.generation++;
      this.playEpoch++;
      this.stopFrames();
      this.videoAbort?.abort();
      if (this.video) { this.video.pause(); this.video.removeAttribute('src'); this.video.load(); this.video.remove(); }
      this.video = null;
      this.pending = false;
      this.blocked = false;
      this.failed = false;
      this.index = index;
      if (userAction) {
        this.userPaused = false;
        this.requiresGesture = false;
        this.started = true;
      }
      this.items.forEach((item, i) => {
        const selected = i === index;
        this.tabs[i].setAttribute('aria-selected', String(selected));
        this.tabs[i].tabIndex = selected ? 0 : -1;
        item.querySelector('[data-progress]').style.transform = 'scaleX(0)';
        this.panels[i].hidden = !selected;
        this.panels[i].classList.remove('is-playing');
      });
      this.querySelector('[data-stage-empty]').hidden = !!this.panels[index].querySelector('.evt-poster');
      this.querySelector('[data-count]').textContent = `${String(index + 1).padStart(2,'0')} / ${String(this.items.length).padStart(2,'0')}`;
      this.revealTab();
      this.primePoster();
      this.setState('ready');
      if (this.started) this.mountVideo();
      this.reconcile();
    }
    revealTab() {
      if (!this.mobile.matches || this.index < 0) return;
      const list = this.querySelector('[role="tablist"]');
      const tab = this.tabs[this.index];
      const left = tab.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
      if (left < list.scrollLeft || left + tab.offsetWidth > list.scrollLeft + list.clientWidth) {
        list.scrollTo({left:Math.max(0,left - 4),behavior:'auto'});
      }
    }
    primePoster() {
      const next = this.panels[(this.index + 1) % this.panels.length]?.querySelector('.evt-poster');
      if (next) next.loading = 'eager';
    }
    mountVideo() {
      if (this.video || !this.started || this.index < 0) return;
      const panel = this.panels[this.index];
      if (!panel.dataset.videoUrl?.trim()) { this.setState(this.emptyState()); return; }
      const fragment = panel.querySelector('[data-video-template]').content.cloneNode(true);
      const video = fragment.querySelector('video');
      this.video = video;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      const poster = panel.querySelector('.evt-poster');
      if (poster) video.poster = poster.currentSrc || poster.src;
      this.videoAbort = new AbortController();
      const signal = this.videoAbort.signal;
      const generation = this.generation;
      const current = () => this.connected && generation === this.generation && video === this.video;
      video.addEventListener('loadedmetadata', () => { if (current()) this.updateProgress(); }, {signal});
      video.addEventListener('canplay', () => { if (current()) this.reconcile(); }, {signal});
      video.addEventListener('timeupdate', () => { if (current()) this.updateProgress(); }, {signal});
      video.addEventListener('ended', () => {
        if (current() && this.canPlay() && !this.userPaused) this.advance();
      }, {signal});
      video.addEventListener('playing', () => {
        if (!current()) return;
        if (!this.canPlay()) { video.pause(); return; }
        panel.classList.add('is-playing');
        this.setState('playing');
        this.startFrames();
      }, {signal});
      ['waiting','stalled'].forEach(name => video.addEventListener(name, () => {
        if (current() && !video.paused) { this.stopFrames(); this.setState('loading'); }
      }, {signal}));
      video.addEventListener('pause', () => {
        if (!current()) return;
        this.stopFrames();
        if (!this.failed && !this.blocked) this.setState(this.userPaused ? 'paused' : 'ready');
      }, {signal});
      video.addEventListener('error', () => {
        if (!current()) return;
        this.failed = true;
        this.stopFrames();
        this.setState('error');
      }, {signal});
      panel.append(fragment);
      video.src = panel.dataset.videoUrl;
      video.load();
    }
    canPlay() {
      return this.connected && this.started && this.inView && !document.hidden && !this.suspended && !this.userPaused && !this.requiresGesture && !this.failed && !this.blocked;
    }
    reconcile() {
      if (!this.connected || this.index < 0) return;
      if (!this.panels[this.index].dataset.videoUrl?.trim()) { this.setState(this.emptyState()); return; }
      if (!this.canPlay()) {
        this.playEpoch++;
        this.pending = false;
        this.video?.pause();
        this.stopFrames();
        this.setState(this.failed ? 'error' : this.blocked ? 'blocked' : this.userPaused ? 'paused' : 'ready');
        return;
      }
      if (this.video?.ended) { this.advance(); return; }
      this.tryPlay();
    }
    async tryPlay() {
      if (!this.video || !this.canPlay() || this.pending || !this.video.paused) return;
      const video = this.video;
      const epoch = ++this.playEpoch;
      this.pending = true;
      this.setState('loading');
      try {
        await video.play();
        if (video !== this.video) { video.pause(); return; }
        if (epoch !== this.playEpoch) return;
        if (!this.canPlay()) video.pause();
      } catch (error) {
        if (video !== this.video || epoch !== this.playEpoch) return;
        if (error.name === 'AbortError') return;
        if (error.name === 'NotAllowedError') { this.blocked = true; this.setState('blocked'); }
        else { this.failed = true; this.setState('error'); }
        this.stopFrames();
      } finally {
        if (video === this.video && epoch === this.playEpoch) this.pending = false;
      }
    }
    updateProgress() {
      if (!this.video || this.index < 0) return;
      const duration = this.video.duration;
      const target = Number.isFinite(duration) && duration > 0 ? Math.min(duration,this.durationLimit) : 0;
      const progress = target > 0 ? Math.min(1,Math.max(0,this.video.currentTime / target)) : 0;
      this.items[this.index].querySelector('[data-progress]').style.transform = `scaleX(${progress})`;
      if (target > 0 && this.video.currentTime >= target && this.canPlay() && (!this.video.paused || this.video.ended) && this.video.readyState >= 2) this.advance();
    }
    startFrames() {
      this.stopFrames();
      const generation = this.generation;
      const tick = () => {
        this.frame = null;
        if (!this.connected || generation !== this.generation || !this.video || this.video.paused || !this.canPlay()) return;
        this.updateProgress();
        if (generation === this.generation) this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    stopFrames() { if (this.frame !== null) cancelAnimationFrame(this.frame); this.frame = null; }
    advance() {
      const next = this.nextPlayable(this.index);
      if (next !== -1) this.select(next);
      else this.setState('missing');
    }
    toggle() {
      if (!this.video || this.video.paused && !this.pending || this.failed || this.blocked) this.resume();
      else {
        this.userPaused = true;
        this.reconcile();
      }
    }
    resume() {
      this.userPaused = false;
      this.requiresGesture = false;
      this.blocked = false;
      this.started = true;
      if (this.failed) { this.select(this.index,true); return; }
      this.mountVideo();
      this.reconcile();
    }
    setState(state) {
      this.dataset.state = state;
      const playing = state === 'playing' || state === 'loading';
      const label = state === 'error' ? this.dataset.retry : playing ? this.dataset.pause : state === 'paused' ? this.dataset.resume : this.dataset.play;
      const unavailable = state === 'empty' || state === 'missing';
      this.control.disabled = unavailable;
      const controls = this.querySelector('[data-stage-controls]');
      if (controls) controls.hidden = unavailable;
      this.control.setAttribute('aria-label',label);
      this.querySelector('[data-control-label]').textContent = label;
      this.querySelector('[data-play-icon]').hidden = playing;
      this.querySelector('[data-pause-icon]').hidden = !playing;
      this.querySelector('[data-start]').setAttribute('aria-label',label);
      this.querySelector('[data-start-label]').textContent = label;
      this.overlay.hidden = !['ready','paused','blocked','error'].includes(state);
      const message = this.dataset[state] || this.dataset.ready;
      if (this.status.textContent !== message) this.status.textContent = message;
    }
    disconnectedCallback() {
      this.connected = false;
      this.generation++;
      this.playEpoch++;
      this.abort?.abort();
      this.videoAbort?.abort();
      this.observer?.disconnect();
      this.contentObserver?.disconnect();
      this.stopFrames();
      if (this.video) { this.video.pause(); this.video.removeAttribute('src'); this.video.load(); this.video.remove(); }
      this.video = null;
    }
  }
  customElements.define('entina-video-features',EntinaVideoFeatures);
})();

